import type { ChangeEvidence, ChangeKind, Row } from '@contract';
import { useState } from 'react';
import { CustomerChip, TransactionChip } from '@/components/money/entity-chips';
import {
  Fact,
  type Resolution,
  ResolveDialog,
} from '@/components/money/resolve-dialog';
import { Money } from '@/components/primitives/money';
import { StatusPill } from '@/components/primitives/status-pill';
import { useCanPropose } from '@/components/propose-change';
import { text } from '@/components/record-table';
import { Button } from '@/components/ui/button';
import { formatDateTime, relativeTime } from '@/lib/format';
import { homeCurrency } from '@/lib/market';

const NOUN = {
  transfer: 'transfer',
  collection: 'mobile money collection',
  payout: 'mobile money payout',
  bill: 'bill payment',
} as const;

type StuckKind = keyof typeof NOUN;

const CHANGE: Record<StuckKind, ChangeKind> = {
  transfer: 'resolve_stuck',
  collection: 'resolve_collection',
  payout: 'resolve_payout',
  bill: 'resolve_bill',
};

/** [post label, post effect, fail label, fail effect] per kind. */
const COPY: Record<StuckKind, [string, string, string, string]> = {
  transfer: [
    'It went through: post it',
    'Finishes the transfer. The sender’s hold becomes the final debit and the receiver is credited.',
    'It did not go through: fail it',
    'Fails the transfer and releases the hold back to the sender. Nothing is credited.',
  ],
  collection: [
    'The money arrived: credit the wallet',
    'Credits the customer’s wallet with the collected amount.',
    'Nothing arrived: fail it',
    'Fails the collection. The wallet is not credited.',
  ],
  payout: [
    'It was paid: post it',
    'Posts the payout as paid. The customer’s hold becomes the final debit.',
    'It failed: refund the customer',
    'Fails the payout and gives the money back to the customer’s wallet.',
  ],
  bill: [
    'The bill was delivered: post it',
    'Posts the bill as paid. The customer’s hold becomes the final debit.',
    'It was not delivered: refund the customer',
    'Fails the bill and gives the money back to the customer’s wallet.',
  ],
};

function stuckKind(row: Row): StuckKind | undefined {
  const kind = text(row.kind).toLowerCase();
  return (Object.keys(NOUN) as StuckKind[]).find((key) => kind.includes(key));
}

function stuckResolutions(row: Row, kind: StuckKind): Resolution[] {
  const target =
    kind === 'collection'
      ? { collection_id: text(row.collection_id) }
      : { transaction_id: text(row.transaction_id) };
  const [postLabel, postEffect, failLabel, failEffect] = COPY[kind];
  return [
    {
      value: 'posted',
      label: postLabel,
      effect: postEffect,
      kind: CHANGE[kind],
      payload: { ...target, outcome: 'posted' },
    },
    {
      value: 'failed',
      label: failLabel,
      effect: failEffect,
      kind: CHANGE[kind],
      payload: { ...target, outcome: 'failed' },
    },
  ];
}

/** Resolve a stuck row: one primary button that opens the dialog. */
export function ResolveStuckRow({ row }: Readonly<{ row: Row }>) {
  const [open, setOpen] = useState(false);
  const kind = stuckKind(row);
  const allowed = useCanPropose(CHANGE[kind ?? 'transfer']);
  if (!kind) return null;
  return (
    <>
      <Button
        size="sm"
        disabled={!allowed}
        title={allowed ? undefined : 'You may not propose this change.'}
        onClick={() => setOpen(true)}
      >
        Resolve…
      </Button>
      {open ? (
        <ResolveDialog
          title={`Resolve a stuck ${NOUN[kind]}`}
          sentence={stuckSentence(row, kind)}
          hint="Check the provider or the core first; the answer is final."
          options={stuckResolutions(row, kind)}
          evidence={evidenceOf(row)}
          facts={<StuckFacts row={row} />}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}

function evidenceOf(row: Row): ChangeEvidence[] {
  return row.transaction_id
    ? [{ type: 'transaction', ref: text(row.transaction_id) }]
    : [];
}

function hoursSince(at: unknown): string {
  const hours = Math.max(
    0,
    Math.round((Date.now() - Date.parse(text(at))) / 3_600_000),
  );
  return hours < 48 ? `${hours} h` : `${Math.round(hours / 24)} days`;
}

function currencyOf(row: Row): string {
  return typeof row.currency === 'string' ? row.currency : homeCurrency();
}

function stuckSentence(row: Row, kind: StuckKind): string {
  const provider = row.provider ? ` from ${text(row.provider)}` : '';
  return `This ${NOUN[kind]} has had no final answer${provider} for ${hoursSince(row.since)}.`;
}

function StuckFacts({ row }: Readonly<{ row: Row }>) {
  const customerId = typeof row.customer_id === 'string' ? row.customer_id : '';
  return (
    <>
      <Fact label="Kind">{text(row.kind)}</Fact>
      <Fact label="Status">
        <StatusPill status={text(row.status)} />
      </Fact>
      {typeof row.amount_minor === 'number' ? (
        <Fact label="Amount">
          <Money minor={row.amount_minor} currency={currencyOf(row)} />
        </Fact>
      ) : null}
      {row.provider ? <Fact label="Provider">{text(row.provider)}</Fact> : null}
      {row.last_answer ? (
        <Fact label="Last answer">{text(row.last_answer)}</Fact>
      ) : null}
      <Fact label="Since">
        {formatDateTime(text(row.since))} ({relativeTime(text(row.since))})
      </Fact>
      {row.transaction_id ? (
        <Fact label="Transaction">
          <TransactionChip id={text(row.transaction_id)} />
        </Fact>
      ) : null}
      {customerId ? (
        <Fact label="Customer">
          <CustomerChip id={customerId} />
        </Fact>
      ) : null}
    </>
  );
}

/** Resolve money held in suspense: release it to the wallet or return it. */
export function ResolveSuspenseRow({ row }: Readonly<{ row: Row }>) {
  const [open, setOpen] = useState(false);
  const allowed = useCanPropose('release_suspense');
  if (row.status !== 'held' || !allowed) return null;
  const transactionId = text(row.transaction_id);
  const options: Resolution[] = [
    {
      value: 'release',
      label: 'Release it to the wallet',
      effect: 'Credits the wallet with this amount.',
      kind: 'release_suspense',
      payload: { transaction_id: transactionId, outcome: 'release' },
    },
    {
      value: 'return',
      label: 'Return it to the sender',
      effect:
        'Sends the money back to whoever sent it. The wallet gets nothing.',
      kind: 'release_suspense',
      payload: { transaction_id: transactionId, outcome: 'return' },
    },
  ];
  return (
    <>
      <Button size="sm" onClick={() => setOpen(true)}>
        Resolve…
      </Button>
      {open ? (
        <ResolveDialog
          title="Decide money in suspense"
          sentence={`${text(row.source) || 'This money'} has been held in suspense for ${hoursSince(row.held_since)}${row.overdue ? ', past its decide-by date' : ''}.`}
          options={options}
          evidence={evidenceOf(row)}
          onClose={() => setOpen(false)}
          facts={
            <>
              <Fact label="Status">
                <StatusPill status={text(row.status)} />
              </Fact>
              {typeof row.amount_minor === 'number' ? (
                <Fact label="Amount">
                  <Money minor={row.amount_minor} currency={currencyOf(row)} />
                </Fact>
              ) : null}
              {row.wallet_number ? (
                <Fact label="Wallet">{text(row.wallet_number)}</Fact>
              ) : null}
              {row.sender ? (
                <Fact label="Sender">{text(row.sender)}</Fact>
              ) : null}
              <Fact label="Held since">
                {formatDateTime(text(row.held_since))}
              </Fact>
              {row.decide_by ? (
                <Fact label="Decide by">
                  {formatDateTime(text(row.decide_by))}
                </Fact>
              ) : null}
              <Fact label="Transaction">
                <TransactionChip id={transactionId} />
              </Fact>
            </>
          }
        />
      ) : null}
    </>
  );
}
